import { pure } from '@core/helpers/pure';
import { TuiTextarea } from '@taiga-ui/kit';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  inject,
  OnInit,
  signal,
} from '@angular/core';
import {
  TuiDialogContext,
  TuiError,
  TuiButton,
  TuiTextfield,
} from '@taiga-ui/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { EMPTY, Subject, switchMap, tap } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NgClass, NgTemplateOutlet } from '@angular/common';
import { POLYMORPHEUS_CONTEXT } from '@taiga-ui/polymorpheus';
import type { RequestProduct } from '@core/types/RequestProduct';
import { RequestService } from '@core/services/request/request.service';
import { RequestProductAndGoodsService } from '@core/services/request/request-product-and-goods.service';
import { catchError } from 'rxjs/operators';
import { TranslatePipe } from '@ngx-translate/core';
import { OutkeepTranslateService } from '@core/services/outkeep-translate.service';
import { CompleteForciblyModalTranslations } from '@core/types';
import type { CompleteForciblyModalTranslationsPayload } from '@core/types';

enum FormFields {
  Reason = 'reason',
}

enum ViewSteps {
  Problems = 'problems',
  Reason = 'reason',
}

@Component({
  selector: 'x-complete-forcibly-modal-form',
  imports: [
    TuiTextfield,
    NgClass,
    TuiButton,
    ReactiveFormsModule,
    TuiError,
    TuiTextarea,
    NgTemplateOutlet,
    TranslatePipe,
  ],
  templateUrl: './complete-forcibly-modal-form.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CompleteForciblyModalFormComponent implements OnInit {
  private readonly outkeepTranslateService = inject(OutkeepTranslateService);
  private readonly formBuilder = inject(FormBuilder);
  private readonly requestService = inject(RequestService);

  private translates: CompleteForciblyModalTranslationsPayload = {
    [CompleteForciblyModalTranslations.FailedToCloseForcibly]:
      'Не удалось принудительно закрыть товар/услугу',
  };

  private readonly requestProductAndGoodsService = inject(
    RequestProductAndGoodsService,
  );

  private readonly destroyRef = inject(DestroyRef);
  private readonly context = inject(POLYMORPHEUS_CONTEXT) as TuiDialogContext<
    void,
    { requestProduct: RequestProduct; problemsText: string }
  >;

  public ViewStepsType = ViewSteps;
  public currentViewStep = ViewSteps.Problems;
  public $errorText = signal('');

  @pure
  private get requestProduct(): RequestProduct {
    return this.context.data.requestProduct;
  }

  @pure
  public get problemsText(): string {
    return this.context.data.problemsText;
  }

  FormFields = FormFields;
  formGroup = this.formBuilder.group({
    [FormFields.Reason]: ['', Validators.required],
  });
  completeForcibly$ = new Subject<void>();

  @pure
  get reasonField() {
    return this.formGroup.controls[FormFields.Reason];
  }

  ngOnInit() {
    if (!this.requestProduct) {
      throw new Error(
        'requestProduct not passed to x-complete-forcibly-modal-form',
      );
    }

    this.outkeepTranslateService
      .translate(Object.values(CompleteForciblyModalTranslations))
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(translates => (this.translates = translates));

    this.completeForcibly$
      .pipe(
        switchMap(() => this.performForciblyCompletion$()),
        switchMap(() => this.completeProduct$()),
        catchError(err => {
          console.error('Error during forcibly completion:', err);
          if (err.error) {
            this.$errorText.set(err.error);
          } else {
            this.$errorText.set(
              this.translates[
                CompleteForciblyModalTranslations.FailedToCloseForcibly
              ]!,
            );
          }

          return EMPTY;
        }),
        tap(() => {
          this.clearSelfState();
          this.requestService.reloadRequest();
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  private performForciblyCompletion$() {
    if (this.formGroup.valid) {
      return this.requestProductAndGoodsService.performForciblyCompletion$(
        this.requestProduct.id,
        this.reasonField.value!,
      );
    }

    return EMPTY;
  }

  private completeProduct$() {
    return this.requestProductAndGoodsService.completeProduct$(
      this.requestProduct.id,
      true,
    );
  }

  clearSelfState() {
    this.formGroup.reset();
    this.context.completeWith();
  }
}
